import { z } from '../kernel/zod.ts';
import { IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { withoutControlChars } from '../minutes/item.ts';
import { DocumentRowSchema } from './documents.ts';

export const ManagerTitleSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .refine((title) => withoutControlChars(title) === title && !/[\n\r\t]/.test(title), {
    message: 'Название должно быть одной строкой без управляющих знаков',
  });
export const ManagerTagsSchema = z.array(z.string().trim().min(1).max(40)).max(30);
export const DocumentManagerNodeSchema = z.object({
  id: IdSchema,
  kind: z.enum(['folder', 'protocol']),
  parentId: IdSchema.nullable(),
  title: z.string(),
  description: z.string(),
  tags: z.array(z.string()),
  favorite: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  deletedAt: TimestampSchema.nullable(),
  createdBy: PersonRefSchema,
  version: z.number().int().min(1),
  canManage: z.boolean(),
  document: DocumentRowSchema.optional(),
});
export type DocumentManagerNode = z.infer<typeof DocumentManagerNodeSchema>;
export const DocumentManagerListSchema = z.object({ items: z.array(DocumentManagerNodeSchema) });
export const CreateManagerFolderBodySchema = z
  .object({ title: ManagerTitleSchema, parentId: IdSchema.nullable().optional() })
  .strict();
export const UpdateManagerNodeBodySchema = z
  .object({
    version: z.number().int().min(1),
    title: ManagerTitleSchema.optional(),
    description: z.string().trim().max(5000).optional(),
    tags: ManagerTagsSchema.optional(),
  })
  .strict();
export const ManagerFavoriteBodySchema = z.object({ favorite: z.boolean() }).strict();
export const ManagerBatchBodySchema = z
  .object({
    action: z.enum(['move', 'trash', 'restore', 'delete', 'tags']),
    items: z
      .array(z.object({ id: IdSchema, version: z.number().int().min(1) }).strict())
      .min(1)
      .max(200),
    parentId: IdSchema.nullable().optional(),
    parents: z.record(IdSchema, IdSchema.nullable()).optional(),
    names: z.record(IdSchema, ManagerTitleSchema).optional(),
    tags: ManagerTagsSchema.optional(),
    mode: z.enum(['add', 'set']).optional(),
  })
  .strict();
export type ManagerBatchBody = z.infer<typeof ManagerBatchBodySchema>;
export const ManagerCommentSchema = z.object({
  id: IdSchema,
  nodeId: IdSchema,
  text: z.string(),
  author: PersonRefSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  version: z.number().int().min(1),
  canEdit: z.boolean(),
});
export type ManagerComment = z.infer<typeof ManagerCommentSchema>;
export const ManagerHistoryEntrySchema = z.object({
  id: IdSchema,
  nodeId: IdSchema,
  action: z.string(),
  text: z.string(),
  actor: PersonRefSchema,
  at: TimestampSchema,
});
export type ManagerHistoryEntry = z.infer<typeof ManagerHistoryEntrySchema>;
export const ManagerActivitySchema = z.object({
  comments: z.array(ManagerCommentSchema),
  history: z.array(ManagerHistoryEntrySchema),
});
export type ManagerActivity = z.infer<typeof ManagerActivitySchema>;
export const CreateManagerCommentBodySchema = z
  .object({ text: z.string().trim().min(1).max(10000) })
  .strict();
export const UpdateManagerCommentBodySchema = CreateManagerCommentBodySchema.extend({
  version: z.number().int().min(1),
}).strict();
export const DeleteManagerCommentBodySchema = z
  .object({ version: z.number().int().min(1) })
  .strict();
export const ManagerCommentParamsSchema = z.object({ id: IdSchema, commentId: IdSchema });
